/**
 * Place一覧フィルタバー（Notion風）
 *
 * 「+ フィルタ」→ 項目選択 → 条件設定
 * アクティブフィルタはチップ形式で表示
 */

import { Calendar, Camera, Hash, Home, MapPin, Plus, Tag, Type, Wifi, X } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import type { Place } from '@/api/types/overland'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import {
  createDefaultFilter,
  DATE_PRESETS,
  type DatePreset,
  FILTER_DEFINITIONS,
  type FilterType,
  type FilterValue,
  getFilterLabel,
  NUMBER_OPERATORS,
  type NumberOperator,
} from './place-filter-logic'

// =============================================================================
// アイコンマップ
// =============================================================================

const FILTER_ICONS: Record<FilterType, React.ReactNode> = {
  is_custom: <Home className="size-3.5" />,
  provider: <MapPin className="size-3.5" />,
  category: <Tag className="size-3.5" />,
  genre: <Tag className="size-3.5" />,
  visit_count: <Hash className="size-3.5" />,
  last_visited: <Calendar className="size-3.5" />,
  has_image: <Camera className="size-3.5" />,
  has_address: <MapPin className="size-3.5" />,
  has_google_id: <MapPin className="size-3.5" />,
  has_wifi: <Wifi className="size-3.5" />,
  has_custom_name: <Type className="size-3.5" />,
}

// =============================================================================
// フィルタバー本体
// =============================================================================

type PlaceFilterBarProps = {
  places: Place[]
  filters: FilterValue[]
  onFiltersChange: (filters: FilterValue[]) => void
}

export const PlaceFilterBar = ({ places, filters, onFiltersChange }: PlaceFilterBarProps) => {
  const [isAddOpen, setIsAddOpen] = useState(false)

  const availableFilters = useMemo(() => {
    const activeTypes = new Set(filters.map((f) => f.type))
    return FILTER_DEFINITIONS.filter((def) => !activeTypes.has(def.type))
  }, [filters])

  const providerOptions = useMemo(() => {
    const set = new Set(places.map((p) => p.provider ?? 'none'))
    return Array.from(set).sort()
  }, [places])

  const categoryOptions = useMemo(() => {
    const set = new Set<string>()
    for (const p of places) {
      for (const c of p.categories) {
        set.add(c.display_name ?? c.name)
      }
    }
    return Array.from(set).sort()
  }, [places])

  const genreOptions = useMemo(() => {
    const set = new Set<string>()
    for (const p of places) {
      for (const g of p.genres) {
        set.add(g.name)
      }
    }
    return Array.from(set).sort()
  }, [places])

  const addFilter = useCallback(
    (type: FilterType) => {
      onFiltersChange([...filters, createDefaultFilter(type)])
      setIsAddOpen(false)
    },
    [filters, onFiltersChange],
  )

  const updateFilter = useCallback(
    (index: number, updated: FilterValue) => {
      const next = [...filters]
      next[index] = updated
      onFiltersChange(next)
    },
    [filters, onFiltersChange],
  )

  const removeFilter = useCallback(
    (index: number) => {
      onFiltersChange(filters.filter((_, i) => i !== index))
    },
    [filters, onFiltersChange],
  )

  const clearAll = useCallback(() => {
    onFiltersChange([])
  }, [onFiltersChange])

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {filters.map((filter, index) => (
        <FilterChip
          key={`${filter.type}-${index}`}
          filter={filter}
          providerOptions={providerOptions}
          categoryOptions={categoryOptions}
          genreOptions={genreOptions}
          onUpdate={(updated) => updateFilter(index, updated)}
          onRemove={() => removeFilter(index)}
        />
      ))}

      {availableFilters.length > 0 && (
        <Popover open={isAddOpen} onOpenChange={setIsAddOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
              <Plus className="size-3" />
              フィルタ
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-1.5">
            <div className="space-y-0.5 max-h-64 overflow-y-auto">
              {availableFilters.map((def) => (
                <button
                  key={def.type}
                  type="button"
                  className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-md hover:bg-accent transition-colors text-left"
                  onClick={() => addFilter(def.type)}
                >
                  {FILTER_ICONS[def.type]}
                  {def.label}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {filters.length > 0 && (
        <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={clearAll}>
          クリア
        </Button>
      )}
    </div>
  )
}

// =============================================================================
// フィルタチップ
// =============================================================================

type FilterChipProps = {
  filter: FilterValue
  providerOptions: string[]
  categoryOptions: string[]
  genreOptions: string[]
  onUpdate: (filter: FilterValue) => void
  onRemove: () => void
}

const FilterChip = ({
  filter,
  providerOptions,
  categoryOptions,
  genreOptions,
  onUpdate,
  onRemove,
}: FilterChipProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const def = FILTER_DEFINITIONS.find((d) => d.type === filter.type)
  if (!def) return null

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80 gap-1 pr-1 text-xs h-7">
          {FILTER_ICONS[filter.type]}
          <span className="font-medium">{def.label}:</span>
          <span>{getFilterLabel(filter)}</span>
          <button
            type="button"
            className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
          >
            <X className="size-3" />
          </button>
        </Badge>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3">
        <FilterEditor
          filter={filter}
          providerOptions={providerOptions}
          categoryOptions={categoryOptions}
          genreOptions={genreOptions}
          onUpdate={(updated) => {
            onUpdate(updated)
            setIsOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

// =============================================================================
// フィルタ条件エディタ
// =============================================================================

type FilterEditorProps = {
  filter: FilterValue
  providerOptions: string[]
  categoryOptions: string[]
  genreOptions: string[]
  onUpdate: (filter: FilterValue) => void
}

const FilterEditor = ({ filter, providerOptions, categoryOptions, genreOptions, onUpdate }: FilterEditorProps) => {
  switch (filter.type) {
    case 'is_custom':
      return (
        <BooleanToggle
          value={filter.value}
          labelTrue="マイプレイス"
          labelFalse="通常"
          onChange={(v) => onUpdate({ type: 'is_custom', value: v })}
        />
      )

    case 'provider':
      return (
        <MultiSelect
          options={providerOptions.map((v) => ({ value: v, label: v === 'none' ? 'なし' : v }))}
          selected={filter.values}
          onChange={(values) => onUpdate({ type: 'provider', values })}
        />
      )

    case 'category':
      return (
        <MultiSelect
          options={categoryOptions.map((v) => ({ value: v, label: v }))}
          selected={filter.values}
          onChange={(values) => onUpdate({ type: 'category', values })}
        />
      )

    case 'genre':
      return (
        <MultiSelect
          options={genreOptions.map((v) => ({ value: v, label: v }))}
          selected={filter.values}
          onChange={(values) => onUpdate({ type: 'genre', values })}
        />
      )

    case 'visit_count':
      return (
        <NumberFilter
          operator={filter.operator}
          value={filter.value}
          onChange={(operator, value) => onUpdate({ type: 'visit_count', operator, value })}
        />
      )

    case 'last_visited':
      return (
        <PresetSelect
          options={DATE_PRESETS}
          value={filter.preset}
          onChange={(preset) => onUpdate({ type: 'last_visited', preset: preset as DatePreset })}
        />
      )

    case 'has_image':
      return (
        <BooleanToggle
          value={filter.value}
          labelTrue="あり"
          labelFalse="なし"
          onChange={(v) => onUpdate({ type: 'has_image', value: v })}
        />
      )

    case 'has_address':
      return (
        <BooleanToggle
          value={filter.value}
          labelTrue="あり"
          labelFalse="なし"
          onChange={(v) => onUpdate({ type: 'has_address', value: v })}
        />
      )

    case 'has_google_id':
      return (
        <BooleanToggle
          value={filter.value}
          labelTrue="あり"
          labelFalse="なし"
          onChange={(v) => onUpdate({ type: 'has_google_id', value: v })}
        />
      )

    case 'has_wifi':
      return (
        <BooleanToggle
          value={filter.value}
          labelTrue="あり"
          labelFalse="なし"
          onChange={(v) => onUpdate({ type: 'has_wifi', value: v })}
        />
      )

    case 'has_custom_name':
      return (
        <BooleanToggle
          value={filter.value}
          labelTrue="あり"
          labelFalse="なし"
          onChange={(v) => onUpdate({ type: 'has_custom_name', value: v })}
        />
      )
  }
}

// =============================================================================
// 入力パーツ
// =============================================================================

const BooleanToggle = ({
  value,
  labelTrue,
  labelFalse,
  onChange,
}: {
  value: boolean
  labelTrue: string
  labelFalse: string
  onChange: (v: boolean) => void
}) => (
  <div className="flex gap-1.5">
    <Button variant={value ? 'default' : 'outline'} size="sm" className="flex-1 text-xs" onClick={() => onChange(true)}>
      {labelTrue}
    </Button>
    <Button
      variant={!value ? 'default' : 'outline'}
      size="sm"
      className="flex-1 text-xs"
      onClick={() => onChange(false)}
    >
      {labelFalse}
    </Button>
  </div>
)

const MultiSelect = ({
  options,
  selected,
  onChange,
}: {
  options: { value: string; label: string }[]
  selected: string[]
  onChange: (values: string[]) => void
}) => {
  const [search, setSearch] = useState('')
  const filtered = options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  return (
    <div className="space-y-2">
      {options.length > 5 && (
        <Input
          placeholder="検索..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-7 text-xs"
        />
      )}
      <div className="max-h-40 overflow-y-auto space-y-0.5">
        {filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-2">該当なし</p>
        ) : (
          filtered.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`flex items-center gap-2 w-full px-2 py-1 text-xs rounded-md transition-colors text-left ${
                selected.includes(opt.value) ? 'bg-primary/10 text-primary' : 'hover:bg-accent'
              }`}
              onClick={() => toggle(opt.value)}
            >
              <div
                className={`size-3.5 rounded-sm border flex items-center justify-center shrink-0 ${
                  selected.includes(opt.value) ? 'bg-primary border-primary' : 'border-muted-foreground/30'
                }`}
              >
                {selected.includes(opt.value) && <span className="text-primary-foreground text-[10px]">✓</span>}
              </div>
              {opt.label}
            </button>
          ))
        )}
      </div>
    </div>
  )
}

const NumberFilter = ({
  operator,
  value,
  onChange,
}: {
  operator: NumberOperator
  value: number
  onChange: (op: NumberOperator, val: number) => void
}) => (
  <div className="flex gap-1.5">
    <div className="flex gap-0.5">
      {NUMBER_OPERATORS.map((op) => (
        <Button
          key={op.value}
          variant={operator === op.value ? 'default' : 'outline'}
          size="sm"
          className="h-7 w-8 text-xs px-0"
          onClick={() => onChange(op.value, value)}
        >
          {op.label}
        </Button>
      ))}
    </div>
    <Input
      type="number"
      min={0}
      value={value}
      onChange={(e) => onChange(operator, Number(e.target.value))}
      className="h-7 text-xs w-20"
    />
  </div>
)

const PresetSelect = ({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[]
  value: string
  onChange: (value: string) => void
}) => (
  <div className="space-y-0.5">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded-md transition-colors text-left ${
          value === opt.value ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-accent'
        }`}
        onClick={() => onChange(opt.value)}
      >
        {opt.label}
      </button>
    ))}
  </div>
)
